import React, { useEffect, useRef, useState } from 'react';
import { View, TouchableOpacity, ActivityIndicator, NativeSyntheticEvent, TextInputKeyPressEventData, Modal } from 'react-native';
import { translate } from '../services/translate';
import { centralAPI, IpAddressNotAllowed, TooManyCodesRequested, TooManyLoginsAttempts, TooManyValidationsRequest, WrongTypedCode } from '../services/central-api';
import { invalidFirebaseToken, notificationService, tryingToCreateFirebaseTokenBeforeAuth } from '../services/notification';
import MyAppText from '../components/MyAppText';
import app from '../services/version.json';
import FormInput from '../components/FormInput';
import FormActions from '../components/FormActions';
import PasswordInput from '../components/PasswordInput';
import { ClientError, NotFound, UnexpectedError } from '../services/central-api/base-service';
import { WindowInformation } from '../services/window-information';
import { createStyleSheet, useStyles } from 'react-native-unistyles';
import CustomButton from '../components/CustomButton';
import { RootStackParamList } from '../typings/Params';
import { NavigationProp, useNavigation } from '@react-navigation/native';
import { OtpInput, OtpInputRef } from 'react-native-otp-entry';
import Toast from 'react-native-toast-message';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes } from '@fortawesome/pro-regular-svg-icons';
import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import ReportAbuseModal from './ReportAbuseModal';
import { parseTextWithBold } from './deac/DeacCommons';
import HoverableText from '../components/HoverableText';
import moment from 'moment';
import SentinelXLogo from '../components/SentinelXLogo';

interface OTPCredential extends Credential {
    code: string;
}

export default function Login() {
    const navigation = useNavigation<NavigationProp<RootStackParamList>>();
    const { styles, theme } = useStyles(stylesheet);
    const [isModalVisible, setModalVisible] = useState(false);
    const [isReportAbuseModalVisible, setReportAbuseModalVisible] = useState(false);
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [errorMessage, setErrorMessage] = useState('');
    const [loading, setLoading] = useState(false);
    const windowInfo = WindowInformation();
    const [twoFactorUser, setTwoFactorCode] = useState<TwoFactorUserData>();
    const [secondsLeft, setSecondsLeft] = useState(60);
    const [twoFactorModalVisible, setTwoFactorModalVisible] = useState(false);
    const otpRef = useRef<OtpInputRef>(null);
    const [isOtpLoading, setIsOtpLoading] = useState<boolean>(false);
    const { isMobileOrTablet } = WindowInformation();

    useEffect(() => {
        (async () => {
            try {
                const isAuthenticated = await centralAPI.isAuthenticated();
                if (isAuthenticated) {
                    await centralAPI.saveLocationToken();
                    // FIXME:
                    // for some reason this is lopping on anonymous page
                    // Menu after getting auth user
                    // forceRedirectToMenu();

                    navigation.reset({
                        index: 0,
                        routes: [{ name: 'Menu' }],
                    });
                }
            } catch (err) {
                console.error(err);
            }
        })();
    }, []);

    function handleKeyDown(e: NativeSyntheticEvent<TextInputKeyPressEventData>) {
        if (e.nativeEvent.key == 'Enter') {
            if (username == '' || password == '') {
                return;
            }
            login();
        }
    }

    async function forgotPassword() {
        try {
            await centralAPI.forgotPassword(username);
            setModalVisible(false);
            setPassword('');
            setUsername('');
        } catch (err) {
            console.error(err);
        }
    }

    useEffect(() => {
        if (secondsLeft <= 0 || !twoFactorModalVisible) {
            return;
        }

        setTimeout(() => {
            setSecondsLeft((prev) => prev - 1);
        }, 1000);

    }, [secondsLeft, twoFactorModalVisible]);

    async function registerFirebaseToken() {

        try {
            await notificationService.registerToken();
            console.log('firebase token registered.');
        } catch (err) {
            if (
                err instanceof invalidFirebaseToken ||
                err instanceof tryingToCreateFirebaseTokenBeforeAuth
            ) {
                console.warn(err);
                return;
            }
            console.error(err);
        }

    }

    /**
     * Registers location token, Firebase token, and resets navigation.
     * Used after successful login or two-factor validation.
     */
    async function finalizeLogin() {
        await centralAPI.saveLocationToken();

        // FIXME:
        // for some reason this is lopping on anonymous page
        // Menu after getting auth user
        // forceRedirectToMenu();
        navigation.reset({
            index: 0,
            routes: [{ name: 'Menu' }],
        });
        await registerFirebaseToken();

    }

    async function login() {
        if (username === '' || password === '') {
            return;
        }
        setLoading(true);
        try {
            const resp = await centralAPI.authenticate({ username, password });
            if (!resp) {
                await finalizeLogin();
            } else {
                setTwoFactorCode(resp);
                setTwoFactorModalVisible(true);
            }
        } catch (err) {
            if (err instanceof ClientError) {
                setErrorMessage(translate(err.message));
            } else if (err instanceof UnexpectedError || err instanceof TypeError) {
                setErrorMessage(translate('unexpectedError'));
            } else if (err instanceof DOMException) {
                setErrorMessage(translate('timeoutLoginException'));
            } else if (err instanceof IpAddressNotAllowed) {
                setErrorMessage(translate(err.message, { ipAddress: err.ipAddress }));
            } else if (err instanceof TooManyLoginsAttempts) {
                const firstAttempt = moment(err.lastAttempt);
                const unlockTime = firstAttempt.clone().add(err.waitTime, 'minutes');
                const now = moment();
                const remainingMs = Math.max(unlockTime.diff(now), 0);
                const duration = moment.duration(remainingMs);
                const minutes = Math.floor(duration.asMinutes());
                const seconds = duration.seconds();
                setErrorMessage(translate(err.message, { minutes: minutes > 0 ? `${minutes}m ${seconds}s` : `${seconds}s` }));
            } else if (err instanceof TooManyCodesRequested) {
                return Toast.show({
                    type: 'sentinelxWarning',
                    text1: translate(err.message),
                });
            }
        } finally {
            setLoading(false);
        }
    }

    async function handleValidate(code: string) {
        try {
            if (!twoFactorUser) {
                return;
            }
            setIsOtpLoading(true);
            await centralAPI.twoFactorValidator({ username, password, code });
            await finalizeLogin();
        } catch (err) {
            if (err instanceof NotFound) {
                return Toast.show({
                    type: 'sentinelxWarning',
                    text1: translate('codeNotFound'),
                });
            }
            if (err instanceof WrongTypedCode) {
                return Toast.show({
                    type: 'sentinelxWarning',
                    text1: translate(err.message),
                });
            }
            if (err instanceof TooManyValidationsRequest) {
                return Toast.show({
                    type: 'sentinelxWarning',
                    text1: translate(err.message),
                });
            }
            if (err instanceof ClientError) {
                return Toast.show({
                    type: 'sentinelxWarning',
                    text1: translate(err.message),
                });
            }
            console.log(err);
            Toast.show({
                type: 'sentinelxError',
                text1: translate('unexpectedError'),
            });
        } finally {
            setIsOtpLoading(false);
        }
    }

    async function onResend() {
        try {
            if (!twoFactorUser || !otpRef.current) {
                return;
            }
            await centralAPI.resendTwoFactorCode({ username, password });
            Toast.show({
                type: 'sentinelxSuccess',
                text1: translate('ActionSuccessfully'),
            });
            otpRef.current?.clear();
        } catch (err) {
            if (err instanceof NotFound) {
                return Toast.show({
                    type: 'sentinelxWarning',
                    text1: translate('codeNotFound'),
                });
            }
            if (err instanceof TooManyCodesRequested) {
                return Toast.show({
                    type: 'sentinelxWarning',
                    text1: translate(err.message),
                });
            }
            if (err instanceof ClientError) {
                return Toast.show({
                    type: 'sentinelxWarning',
                    text1: translate(err.message),
                });
            }
            console.log(err);
            Toast.show({
                type: 'sentinelxError',
                text1: translate('unexpectedError'),
            });
        }
    }

    useEffect(() => {
        if (!twoFactorModalVisible || !('credentials' in navigator)) {
            return;
        }

        const ac = new AbortController();

        navigator.credentials
            .get({
                otp: { transport: ['sms'] },
                signal: ac.signal,
            } as CredentialRequestOptions)
            .then((otp) => {
                const credential = otp as OTPCredential | null;

                if (credential && credential.code) {
                    handleValidate(credential.code);
                }
            })
            .catch((err) => {
                console.warn('WebOTP failed:', err);
            });

        return () => ac.abort();
    }, [twoFactorModalVisible]);

    return (
        <View style={{ flex: 1, backgroundColor: theme.colors.white['500'] }}>
            <View style={styles.container}>
                <View style={{
                    height: 60,
                    paddingVertical: 12,
                    width: isMobileOrTablet ? 350 : 400,
                }}>
                    <SentinelXLogo />
                </View>
                <View style={styles.formContainer}>
                    <FormInput
                        autoComplete='username-new'
                        label={translate('emailOrRegistry')}
                        placeholder=''
                        value={username}
                        onChangeText={setUsername}
                        invalid={() => false}
                        keyboardType='email-address'
                        onKeyPress={handleKeyDown}
                    />
                    <View style={{ flex: 1, gap: 8 }}>
                        <PasswordInput
                            label={translate('password')}
                            placeholder=''
                            value={password}
                            onChangeText={setPassword}
                            invalid={() => password === ''}
                            onKeyPress={handleKeyDown}
                        />
                        <HoverableText
                            style={{ alignSelf: 'flex-end' }}
                            text={translate('forgotPassword')}
                            textStyle={styles.forgotPasswordLabel}
                            onPress={() => setModalVisible(true)}
                        />
                    </View>
                    {errorMessage ?
                        <View>
                            <MyAppText style={styles.error}>{errorMessage}</MyAppText>
                        </View>
                        : null}
                    {loading ?
                        <View style={{ height: 40 }}>
                            <ActivityIndicator size='small' color={theme.colors.iconColor} />
                        </View>
                        : <CustomButton text={translate('login')} onPress={login} />
                    }
                </View>
            </View>
            <View style={[styles.footer(windowInfo.isMobile), windowInfo.isMobile && { gap: 16 }]}>
                <View style={{ minWidth: 101 }}></View>
                <View style={styles.footerActions(windowInfo.isMobile)}>
                    <HoverableText
                        text={translate('contactUs')}
                        textStyle={styles.footerLabels}
                        onPress={() => navigation.navigate('PublicFrequentlyAskedQuestions')}
                    />
                    <HoverableText
                        text={translate('reportAbuse')}
                        textStyle={styles.footerLabels}
                        onPress={() => setReportAbuseModalVisible(true)}
                    />
                </View>
                <View style={{ maxWidth: 101 }}>
                    <MyAppText style={styles.versionLabel}>{translate('version')}: {app[0].version}</MyAppText>
                </View>
            </View>
            <Modal visible={isModalVisible} animationType='fade' onRequestClose={() => setModalVisible(false)} transparent={true}>
                <View style={styles.modalContainer}>
                    <View style={styles.modalBody}>
                        <View style={{ rowGap: 10 }}>
                            <MyAppText style={styles.modalHeaderText}>
                                {translate('forgotYourPassword')}
                            </MyAppText>
                            <MyAppText style={styles.modalBodyText}>
                                {parseTextWithBold(translate('emailToRedefinePassword'))}
                            </MyAppText>
                        </View>
                        <View style={{ gap: 8 }}>
                            <FormInput
                                label={translate('email')}
                                placeholder={translate('email')}
                                value={username}
                                onChangeText={setUsername}
                                invalid={() => false}
                            />
                            <HoverableText
                                style={{ alignSelf: 'flex-end' }}
                                text={translate('problemsWithAccess')}
                                textStyle={styles.forgotPasswordLabel}
                                onPress={() => navigation.navigate('PublicFrequentlyAskedQuestions')}
                            />
                        </View>
                        <FormActions
                            onSubmit={forgotPassword}
                            onClose={() => setModalVisible(false)}
                            disabled={username === ''}
                            confirmText={'send'} />
                    </View>
                </View>
            </Modal>
            {twoFactorUser && <Modal visible={twoFactorModalVisible} animationType='fade' onRequestClose={() => setTwoFactorModalVisible(false)} transparent={true}>
                <View style={styles.modalContainer}>
                    <View style={styles.twoFactorModalBody}>
                        {isOtpLoading ? <ActivityIndicator size='large' color={theme.colors.loadingColor} /> :
                            <>
                                <View style={{ flexDirection: 'row', justifyContent: 'flex-end' }}>
                                    <TouchableOpacity onPress={() => setTwoFactorModalVisible(false)}>
                                        <FontAwesomeIcon color={theme.colors.iconColor} fontSize={16} icon={faTimes as IconDefinition} />
                                    </TouchableOpacity>
                                </View>
                                <View style={{ alignItems: 'center', gap: 20 }}>
                                    <MyAppText style={styles.headerText}>
                                        {translate('enterTwoFactorCode', { phone: twoFactorUser.phone })}
                                    </MyAppText>
                                    <OtpInput
                                        ref={otpRef}
                                        numberOfDigits={6}
                                        onFilled={handleValidate}
                                        focusColor={theme.colors.field.border}
                                        theme={{
                                            pinCodeTextStyle: { color: theme.colors.labelColor },
                                            pinCodeContainerStyle: { borderColor: theme.colors.card.border }
                                        }}
                                        type='numeric'
                                    />
                                    <TouchableOpacity
                                        onPress={() => {
                                            onResend();
                                            setSecondsLeft(60);
                                        }}
                                        disabled={secondsLeft > 0}
                                    >
                                        <MyAppText style={[{ color: '#007bff' }, secondsLeft != 0 ? {} : { textDecorationLine: 'underline' }]}>
                                            {secondsLeft > 0
                                                ? translate('resendCodeIn', { time: secondsLeft })
                                                : translate('resendCode')}
                                        </MyAppText>
                                    </TouchableOpacity>
                                </View>
                            </>
                        }
                    </View>
                </View>
            </Modal>}
            <ReportAbuseModal isModalVisible={isReportAbuseModalVisible} onClose={() => setReportAbuseModalVisible(false)} />
        </View>
    );
}

const stylesheet = createStyleSheet((theme) => ({
    container: {
        flex: 1,
        justifyContent: 'center',
        alignItems: 'center',
        gap: 50,
    },
    formContainer: {
        flexDirection: 'column',
        gap: 30,
        maxWidth: 375,
        width: '100%',
        padding: 5
    },
    forgotPasswordLabel: {
        color: theme.colors.primary['500'],
        fontSize: 16,
        fontWeight: '600'
    },
    footer: (isMobile) => ({
        flexDirection: isMobile ? 'column' : 'row',
        minHeight: 43,
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: theme.colors.backgrounds['BG-2'],
        padding: isMobile ? 4 : 12,
        paddingBottom: isMobile ? 14 : 12,
        paddingTop: isMobile ? 6 : 12
    }),
    footerActions: (isMobile) => ({
        flexDirection: isMobile ? 'column' : 'row',
        flexGrow: 2,
        gap: isMobile ? 16 : 45,
        alignItems: 'center',
        justifyContent: 'center'
    }),
    footerLabels: {
        color: theme.colors.primary['500'],
        fontSize: 14,
        fontWeight: '600'
    },
    versionLabel: {
        color: theme.colors.neutral['400'],
        fontSize: 14,
        fontWeight: '500'
    },
    error: {
        color: 'red',
        alignSelf: 'center',
        height: 20,
        flex: 1
    },
    modalContainer: {
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        backgroundColor: '#31313199',
        flex: 1,
    },
    modalBody: {
        backgroundColor: theme.colors.container.background,
        flexDirection: 'column',
        borderRadius: 8,
        padding: 32,
        rowGap: 24,
        maxWidth: 570,
    },
    modalHeaderText: {
        fontSize: 24,
        color: theme.colors.labelColor,
        fontWeight: 'bold'
    },
    modalBodyText: {
        fontSize: 14,
        color: theme.colors.disabledText,
    },
    headerText: {
        fontSize: 16,
        color: theme.colors.neutral['500'],
        fontWeight: '600',
        textAlign: 'center'
    },
    twoFactorModalBody: {
        backgroundColor: theme.colors.card.background,
        borderColor: theme.colors.card.border,
        padding: 25,
        width: 350,
        borderRadius: 8,
        borderWidth: 1,
    },
}));
